import * as IoC from 'typescript-ioc';
import { IApiClient, RequestContentType } from '../../infrastructure/api-client';
import {
    IAssignRoomRequestParams,
    IEventMeetingsDTO,
    IGetMeetingsRequestParams,
    IImportMeetingsRequestParams,
    IMeetingDetailsDTO,
    IMeetingRequestParams,
    IMeetingSaveDTO,
    IMeetingsDAO,
    IStartDateTimesDTO,
    IUpdateMeetingAttendees,
} from '../meetings.dao';
import { IEventRequestParams, IWithExternalIdEventRequestParams, replaceSortRequestParamsWithSortString } from '../shared.dto';
import { IApiDateConverter } from '../../infrastructure/api-date-converter';
import { RetypePartial } from '../../infrastructure/type-extensions';
import * as MEETINGS_API from '../../constants/api/meetings-api.constants';
import { JobDtoType } from '../../domain/jobs';
import { TGetArtifactsSourcesRequestParams } from '../artifacts.dao';

type IMeetingDetailsDTOInternal = RetypePartial<IMeetingDetailsDTO, 'startDateTime' | 'endDateTime' | 'goLiveDateTime', string>;
type IMeetingSaveDTOInternal = RetypePartial<IMeetingSaveDTO, 'startDateTime' | 'endDateTime' | 'goLiveDateTime', string>;
interface IEventMeetingsDTOInternal {
    content: IMeetingDetailsDTOInternal[];
    totalElements: number;
}

class MeetingsDAO extends IMeetingsDAO {
    constructor(
        @IoC.Inject private readonly apiClient: IApiClient,
        @IoC.Inject private readonly apiDateConverter: IApiDateConverter,
    ) {
        super();
    }

    public async getEventMeetings(params: IGetMeetingsRequestParams, signal?: AbortSignal): Promise<IEventMeetingsDTO> {
        const paramsWithSortString = replaceSortRequestParamsWithSortString(params);
        const response = await this.apiClient.get<IEventMeetingsDTOInternal>(MEETINGS_API.MEETINGS_LIST_URL, paramsWithSortString, { signal: signal });
        return {
            ...response.parsedResponse,
            content: response.parsedResponse.content.map(x => this.mapInternalMeetingDetailsDTOToPublicDTO(x)),
        };
    }

    public async getMeetingDetails(params: IMeetingRequestParams): Promise<IMeetingDetailsDTO> {
        const response = await this.apiClient.get<IMeetingDetailsDTOInternal>(MEETINGS_API.MEETINGS_DETAILS_URL, params);
        // eslint-disable-next-line no-unused-vars
        const { presenterPortalLink, ...parsedResponse } = response.parsedResponse;

        const result = {
            ...parsedResponse,
            presenters: parsedResponse.presenters.map(organization => ({
                ...organization,
                // eslint-disable-next-line no-unused-vars
                attendees: organization.attendees.map(({ presenterPortalAccessEnabled, ...attendee }) => attendee)
            })),
            hosts: parsedResponse.hosts.map(organization => ({
                ...organization,
                // eslint-disable-next-line no-unused-vars
                attendees: organization.attendees.map(({ presenterPortalAccessEnabled, ...attendee }) => attendee)
            })),
        };

        return this.mapInternalMeetingDetailsDTOToPublicDTO(result);
    }

    public async updateMeetingDetails(meetingDetails: IMeetingDetailsDTO, params: IMeetingRequestParams) {
        const response = await this.apiClient.put<IMeetingDetailsDTOInternal, IMeetingSaveDTOInternal>(MEETINGS_API.MEETINGS_DETAILS_URL,
            {
                ...meetingDetails,
                ...params,
                startDateTime: this.apiDateConverter.dateToAPIString(meetingDetails.startDateTime),
                endDateTime: this.apiDateConverter.dateToAPIString(meetingDetails.endDateTime),
                goLiveDateTime: meetingDetails.goLiveDateTime ? this.apiDateConverter.dateToAPIString(meetingDetails.goLiveDateTime) : null,
            });
        return this.mapInternalMeetingDetailsDTOToPublicDTO(response.parsedResponse);
    }

    public async createMeeting(dateTimeList: IMeetingSaveDTO[], params: IEventRequestParams): Promise<void> {
        const convertedDates = dateTimeList.map(value => ({
            ...value,
            startDateTime: this.apiDateConverter.dateToAPIString(value.startDateTime),
            endDateTime: this.apiDateConverter.dateToAPIString(value.endDateTime),
            goLiveDateTime: value.goLiveDateTime ? this.apiDateConverter.dateToAPIString(value.goLiveDateTime) : null,
        }));
        const url = MEETINGS_API.CREATE_MEETING_URL
            .replace('{accountId}', params.accountId.toString())
            .replace('{eventId}', params.eventId.toString());
        await this.apiClient.post<IMeetingSaveDTOInternal>(url,
            convertedDates,
        );
    }

    public async importMeetings(params: IImportMeetingsRequestParams & IEventRequestParams) {
        const response = await this.apiClient.post<JobDtoType>(MEETINGS_API.MEETINGS_IMPORT_URL, {
            accountId: params.accountId,
            eventId: params.eventId,
            file: params.file
        }, {
            ContentType: RequestContentType.formData,
            uriParams: {
                timezone: params.timezone,
                defaultDuration: params.defaultDuration,
                separator: params.separator,
                dateFormatType: params.dateFormatType,
                connectionVersion: params.connectionVersion,
            }
        });

        return response.parsedResponse;
    }
    /**
     * method automatically downloads a file when receiving a response from the API.
     */
    public async getMeetingsCSV(params: IEventRequestParams) {
        await this.apiClient.getFile(MEETINGS_API.CSV_DOWNLOAD_URL, params);
    }

    public async getRegistrantReportCSV(params: IEventRequestParams) {
        await this.apiClient.getFile(MEETINGS_API.REGISTRANT_REPORT_CSV_DOWNLOAD_URL, params);
    }

    public async getMeetingDetailsByExternalId(params: IWithExternalIdEventRequestParams): Promise<IMeetingDetailsDTO> {
        const response = await this.apiClient.get<IMeetingDetailsDTO>(MEETINGS_API.MEETING_DETAILS_BY_EXTERNAL_ID, params);
        return response.parsedResponse;
    }

    public async assignRooms(params: IAssignRoomRequestParams): Promise<JobDtoType> {
        const response = await this.apiClient.post<JobDtoType>(MEETINGS_API.ASSIGN_ROOMS_URL, { ...params },
            {
            uriParams: {
                connectionVersion: params.connectionVersion,
            }
        });
        return response.parsedResponse;
    }

    public async deleteMeetingInEvent(params: IMeetingRequestParams) {
        const response = await this.apiClient.delete<JobDtoType>(MEETINGS_API.DELETE_MEETING_IN_EVENT, params);
        return response.parsedResponse;
    }

    private mapInternalMeetingDetailsDTOToPublicDTO(internalMeetingsDTO: IMeetingDetailsDTOInternal) {
        return {
            ...internalMeetingsDTO,
            startDateTime: this.apiDateConverter.apiStringToDate(internalMeetingsDTO.startDateTime),
            endDateTime: this.apiDateConverter.apiStringToDate(internalMeetingsDTO.endDateTime),
            goLiveDateTime: internalMeetingsDTO.goLiveDateTime ? this.apiDateConverter.apiStringToDate(internalMeetingsDTO.goLiveDateTime) : null,
        };
    }

    public async getMeetingsStartDateTimes(params: IEventRequestParams): Promise<IStartDateTimesDTO> {
        const response = await this.apiClient.get<IStartDateTimesDTO>(MEETINGS_API.MEETING_START_DATE_TIMES_URL, params);
        return response.parsedResponse;
    }

    public async getScheduledMeetingsStartDateTimes(params: IEventRequestParams): Promise<IStartDateTimesDTO> {
        const response = await this.apiClient.get<IStartDateTimesDTO>(
            MEETINGS_API.MEETING_SCHEDULED_START_DATE_TIMES_URL,
            params
        );
        return response.parsedResponse;
    }

    public async cancelMeetingsByStartDateTimes(params: IEventRequestParams & IStartDateTimesDTO) {
        const response = await this.apiClient.post<JobDtoType>(MEETINGS_API.CANCEL_BY_START_DATE_TIMES, params);
        return response.parsedResponse;
    }

    public async removeMeetingsByStartDateTimes(params: IEventRequestParams & IStartDateTimesDTO) {
        const response = await this.apiClient.post<JobDtoType>(MEETINGS_API.REMOVE_BY_START_DATE_TIMES, params);
        return response.parsedResponse;
    }

    public async updateMeetingAttendees(meetingAttendees: IUpdateMeetingAttendees, params: IMeetingRequestParams): Promise<IMeetingDetailsDTO> {
        const response = await this.apiClient.post<IMeetingDetailsDTOInternal>(MEETINGS_API.MEETING_ATTENDEES_URL, {
            ...meetingAttendees,
            ...params
        });
        return this.mapInternalMeetingDetailsDTOToPublicDTO(response.parsedResponse);
    }

    public async deleteLink(linkId, params) {
        const response = await this.apiClient.delete<void>(MEETINGS_API.DELETE_LINK_URL, { ...params, linkId: linkId });
        return response.parsedResponse;
    }

    public async getEventArtifactsSources(params: TGetArtifactsSourcesRequestParams): Promise<IEventMeetingsDTO> {
        const paramsWithSortString = replaceSortRequestParamsWithSortString(params);
        const response = await this.apiClient.get<IEventMeetingsDTOInternal>(MEETINGS_API.ARTIFACTS_SOURCES_URL, paramsWithSortString);
        return {
            ...response.parsedResponse,
            content: response.parsedResponse.content.map(x => this.mapInternalMeetingDetailsDTOToPublicDTO(x)),
        };
    }
}

export { MeetingsDAO };
